import { Injectable, inject, DOCUMENT } from '@angular/core';

/**
 * Prefijo de las «bases falsas» que el front guardaba en `localStorage`
 * mientras el API no tenía el respaldo real (equipos, jefaturas firmantes, PIN
 * de marcación, …; ver docs/FRONTEND-HARDCODED.md). Retirados esos datos, lo
 * que haya quedado en los navegadores no debe volver a leerse nunca.
 */
export const LEGACY_FAKE_STORE_PREFIX = 'sc-hc-';

/**
 * Limpieza única, al arrancar la app, de las claves `sc-hc-*` que dejaron las
 * bases falsas del front. Es idempotente: si no queda ninguna, no hace nada.
 * Un `localStorage` inaccesible (modo privado, política del navegador) nunca
 * impide el arranque.
 */
@Injectable({ providedIn: 'root' })
export class LegacyStorageCleanupService {
  private readonly document = inject(DOCUMENT);

  /** Borra las claves `sc-hc-*`; devuelve cuántas borró. */
  purge(storage: Storage | null = this.defaultStorage()): number {
    if (!storage) return 0;
    try {
      // Primero se juntan las claves: borrar mientras se recorre por índice
      // corre las posiciones y deja claves sin revisar.
      const keys: string[] = [];
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (key?.startsWith(LEGACY_FAKE_STORE_PREFIX)) keys.push(key);
      }
      keys.forEach((key) => storage.removeItem(key));
      return keys.length;
    } catch {
      return 0;
    }
  }

  private defaultStorage(): Storage | null {
    try {
      return this.document.defaultView?.localStorage ?? null;
    } catch {
      return null;
    }
  }
}

/** APP_INITIALIZER: corre la limpieza una vez, antes de la primera navegación. */
export function initializeLegacyStorageCleanup(): () => void {
  const cleanup = inject(LegacyStorageCleanupService);
  return () => {
    cleanup.purge();
  };
}
